const normalizePaymentMethod = (value) => String(value || '').trim().toLowerCase();

const isBankTransfer = (paymentMethod) => normalizePaymentMethod(paymentMethod) === 'bank transfer';

const isPayOnDelivery = (paymentMethod) =>
  normalizePaymentMethod(paymentMethod) === 'pay on delivery';

const isCardOnDelivery = (paymentMethod) =>
  normalizePaymentMethod(paymentMethod) === 'card on delivery (pos)' ||
  normalizePaymentMethod(paymentMethod) === 'card on delivery';

const isOnDeliveryWorkflow = (paymentMethod) =>
  isPayOnDelivery(paymentMethod) || isCardOnDelivery(paymentMethod);

const buildStep = (key, label, message) => ({ key, label, message });

const withStepStates = (steps, currentIndex) =>
  steps.map((step, index) => ({
    ...step,
    state: index < currentIndex ? 'completed' : index === currentIndex ? 'current' : 'upcoming',
  }));

const BANK_TRANSFER_STEPS = [
  buildStep(
    'order_placed',
    'Order Placed',
    'Your order has been received. We are reviewing your transfer receipt.'
  ),
  buildStep(
    'payment_confirmation',
    'Payment Confirmation',
    'Your payment is currently being reviewed.'
  ),
  buildStep(
    'payment_confirmed',
    'Payment Confirmed',
    'Your payment has been confirmed.\n\nYour order will be prepared shortly.'
  ),
  buildStep(
    'order_in_progress',
    'Order In Progress',
    'Your order is being prepared.\n\nYou will receive a call from our rider shortly.'
  ),
  buildStep(
    'completed',
    'Completed',
    'Your order has been completed successfully. Thank you for choosing Vemmac Kitchen.'
  ),
];

const BANK_TRANSFER_REJECTION_STEPS = [
  buildStep(
    'order_placed',
    'Order Placed',
    'Your order has been received. We are reviewing your transfer receipt.'
  ),
  buildStep(
    'payment_confirmation',
    'Payment Confirmation',
    'Your payment is currently being reviewed.'
  ),
  buildStep(
    'payment_rejected',
    'Payment Rejected',
    'Your payment could not be verified.\n\nPlease contact support or submit a valid receipt.'
  ),
];

const buildBankTransferTracking = (order) => {
  const status = String(order.status || 'pending').toLowerCase();
  const paymentStatus = String(order.payment_status || 'pending_verification').toLowerCase();

  if (status === 'cancel') {
    return {
      workflow: 'bank_transfer',
      steps: BANK_TRANSFER_STEPS.map((step) => ({ ...step, state: 'upcoming' })),
      current_step: 'Cancelled',
      message: 'This order has been cancelled.',
      is_cancelled: true,
    };
  }

  if (paymentStatus === 'rejected') {
    return {
      workflow: 'bank_transfer',
      steps: withStepStates(BANK_TRANSFER_REJECTION_STEPS, 2),
      current_step: BANK_TRANSFER_REJECTION_STEPS[2].label,
      message: BANK_TRANSFER_REJECTION_STEPS[2].message,
      is_cancelled: false,
    };
  }

  if (status === 'completed') {
    return {
      workflow: 'bank_transfer',
      steps: withStepStates(BANK_TRANSFER_STEPS, BANK_TRANSFER_STEPS.length),
      current_step: BANK_TRANSFER_STEPS[4].label,
      message: BANK_TRANSFER_STEPS[4].message,
      is_cancelled: false,
    };
  }

  if (status === 'processing') {
    return {
      workflow: 'bank_transfer',
      steps: withStepStates(BANK_TRANSFER_STEPS, 3),
      current_step: BANK_TRANSFER_STEPS[3].label,
      message: BANK_TRANSFER_STEPS[3].message,
      is_cancelled: false,
    };
  }

  if (paymentStatus === 'verified') {
    return {
      workflow: 'bank_transfer',
      steps: withStepStates(BANK_TRANSFER_STEPS, 2),
      current_step: BANK_TRANSFER_STEPS[2].label,
      message: BANK_TRANSFER_STEPS[2].message,
      is_cancelled: false,
    };
  }

  return {
    workflow: 'bank_transfer',
    steps: withStepStates(BANK_TRANSFER_STEPS, 1),
    current_step: BANK_TRANSFER_STEPS[1].label,
    message: BANK_TRANSFER_STEPS[1].message,
    is_cancelled: false,
  };
};

const buildOnDeliveryTracking = (order) => {
  const status = String(order.status || 'pending').toLowerCase();

  const steps = [
    buildStep(
      'order_placed',
      'Order Placed',
      'Your order has been received and is awaiting confirmation.'
    ),
    buildStep(
      'order_confirmed',
      'Order Confirmed',
      'Your order has been confirmed. You will receive a call from our rider shortly.'
    ),
    buildStep(
      'completed',
      'Completed',
      'Your order has been completed successfully. Thank you for choosing Vemmac Kitchen.'
    ),
  ];

  if (status === 'cancel') {
    return {
      workflow: 'on_delivery',
      steps: steps.map((step) => ({ ...step, state: 'upcoming' })),
      current_step: 'Cancelled',
      message: 'This order has been cancelled.',
      is_cancelled: true,
    };
  }

  if (status === 'completed') {
    return {
      workflow: 'on_delivery',
      steps: withStepStates(steps, steps.length),
      current_step: steps[2].label,
      message: steps[2].message,
      is_cancelled: false,
    };
  }

  if (status === 'processing') {
    return {
      workflow: 'on_delivery',
      steps: withStepStates(steps, 1),
      current_step: steps[1].label,
      message: steps[1].message,
      is_cancelled: false,
    };
  }

  return {
    workflow: 'on_delivery',
    steps: withStepStates(steps, 0),
    current_step: steps[0].label,
    message: steps[0].message,
    is_cancelled: false,
  };
};

export function buildOrderTracking(order = {}) {
  const paymentMethod = order.payment_method || order.paymentMethod || '';

  if (isBankTransfer(paymentMethod)) {
    return buildBankTransferTracking(order);
  }

  if (isOnDeliveryWorkflow(paymentMethod)) {
    return buildOnDeliveryTracking(order);
  }

  return buildOnDeliveryTracking(order);
}
